import { MoreOutlined, StarFilled, StarOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Table, type TableProps, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { availableStatusOptions } from "../helpers/available-status-options.ts"
import { documentTypeIconLookup } from "../helpers/document-type-visuals.ts"
import {
  renderDocumentTypeDecoration,
  renderStatusDecoration
} from "../helpers/render-metadata-decoration.tsx"
import type { DocumentTypeOption } from "../hooks/useDocumentTypeOptions.ts"
import type { StatusOption } from "../hooks/useStatusOptions.ts"
import { MetadataSelect } from "./MetadataSelect.tsx"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

interface FilesListViewProps {
  items: FileItem[]
  actions: (file: FileItem) => MenuItemType[]
  /** Omit both together for a view with no bulk actions behind it (Trash) —
   *  a selection checkbox nothing acts on would be exactly the kind of fake
   *  control the Files feature explicitly avoids. */
  selectedKeys?: Set<string>
  onToggleSelect?: (file: FileItem) => void
  onPreview: (file: FileItem) => void
  isFavorite?: (file: FileItem) => boolean
  onToggleFavorite?: (file: FileItem) => void
  /** Present only where the caller can edit a file's classification (`canMove`) — any source. */
  documentTypeOptions?: DocumentTypeOption[]
  documentTypesFailed?: boolean
  onChangeDocumentType?: (file: FileItem, value: string | null) => Promise<boolean>
  statusOptions?: StatusOption[]
  statusesFailed?: boolean
  onChangeStatus?: (file: FileItem, value: string | null) => Promise<boolean>
  /** Trash: swaps Document Type/Status/Version for Deleted by/Deleted — those
   *  three describe a live file's classification and history, neither of
   *  which is what a trashed row's row is about, and none are editable here
   *  regardless of `onChangeDocumentType`/`onChangeStatus`. Same table, same
   *  Actions-column mechanism (the caller's own `actions(file)` supplies
   *  Restore/Delete permanently instead of the normal menu) — not a second
   *  File Table implementation. */
  trashMode?: boolean
}

const EMPTY_CELL = "—"

export function FilesListView({
  items,
  actions,
  selectedKeys,
  onToggleSelect,
  onPreview,
  isFavorite,
  onToggleFavorite,
  documentTypeOptions = [],
  documentTypesFailed = false,
  onChangeDocumentType,
  statusOptions = [],
  statusesFailed = false,
  onChangeStatus,
  trashMode = false
}: FilesListViewProps) {
  const { t } = useTranslation()
  const documentTypeIcons = useMemo(
    () => documentTypeIconLookup(documentTypeOptions),
    [documentTypeOptions]
  )

  const classificationColumns: NonNullable<TableProps<FileItem>["columns"]> = trashMode
    ? [
        {
          title: t("files.trash.columns.deletedBy"),
          key: "deletedBy",
          render: (_, file) => file.deletedByName || EMPTY_CELL
        },
        {
          title: t("files.trash.columns.deleted"),
          key: "deleted",
          render: (_, file) => getFormatDate(file.deletedAt) ?? EMPTY_CELL
        }
      ]
    : [
        {
          title: t("files.columns.documentType"),
          key: "documentType",
          responsive: ["lg"],
          render: (_, file) => {
            if (!file.canMove || !onChangeDocumentType) {
              return file.documentType ? (
                <Flex align="center" gap={6}>
                  {renderDocumentTypeDecoration(
                    file.documentType,
                    documentTypeIcons[file.documentType]
                  )}
                  <Text>{file.documentTypeLabel}</Text>
                </Flex>
              ) : (
                <Text type="secondary">{t("files.documentType.notAssigned")}</Text>
              )
            }
            return (
              <MetadataSelect
                size="small"
                ariaLabel={t("files.columns.documentType")}
                value={file.documentType}
                options={documentTypeOptions}
                renderDecoration={(value) =>
                  renderDocumentTypeDecoration(value, documentTypeIcons[value])
                }
                loadFailedText={
                  documentTypesFailed ? t("files.documentType.loadFailed") : undefined
                }
                placeholder={t("files.documentType.assignType")}
                className="min-w-32"
                onChange={(value) => onChangeDocumentType(file, value)}
              />
            )
          }
        },
        {
          title: t("files.columns.status"),
          key: "status",
          responsive: ["lg"],
          render: (_, file) => {
            if (!file.canMove || !onChangeStatus) {
              return file.status ? (
                <Flex align="center" gap={6}>
                  {renderStatusDecoration(file.status)}
                  <Text>{file.statusLabel}</Text>
                </Flex>
              ) : (
                <Text type="secondary">{t("files.status.notAssigned")}</Text>
              )
            }
            return (
              <MetadataSelect
                size="small"
                ariaLabel={t("files.columns.status")}
                value={file.status}
                options={availableStatusOptions(statusOptions, file)}
                renderDecoration={renderStatusDecoration}
                disabled={!file.nextStatuses?.length}
                loadFailedText={statusesFailed ? t("files.status.loadFailed") : undefined}
                placeholder={t("files.status.setStatus")}
                className="min-w-32"
                onChange={(value) => onChangeStatus(file, value)}
              />
            )
          }
        },
        {
          title: t("files.columns.version"),
          key: "version",
          responsive: ["lg"],
          render: (_, file) =>
            file.version !== undefined
              ? t("fileViewer.detailsPanel.versionShort", { version: file.version })
              : EMPTY_CELL
        },
        {
          title: t("files.columns.modified"),
          key: "modified",
          render: (_, file) => getFormatDate(file.updated) ?? EMPTY_CELL
        }
      ]

  const columns: TableProps<FileItem>["columns"] = [
    {
      title: t("files.columns.name"),
      key: "name",
      align: "left",
      render: (_, file) => (
        <Flex align="center" gap="small">
          {onToggleFavorite && (
            <Button
              type="text"
              size="small"
              aria-label={t("files.actions.favorite", { name: file.name })}
              icon={
                isFavorite?.(file) ? (
                  <StarFilled className="text-warning" />
                ) : (
                  <StarOutlined />
                )
              }
              onClick={() => onToggleFavorite(file)}
            />
          )}
          <span className="[&_svg]:text-base">{getFileIcon(file.mimeType)}</span>
          <a onClick={() => onPreview(file)}>
            <Text ellipsis={{ tooltip: file.name }}>{file.name}</Text>
          </a>
        </Flex>
      )
    },
    {
      title: t("files.columns.type"),
      key: "type",
      render: (_, file) =>
        file.fileTypeLabel ??
        (file.source === "album-attachment"
          ? t("files.columns.typeFile")
          : t("files.columns.typeDocument"))
    },
    {
      title: t("files.columns.size"),
      key: "size",
      render: (_, file) => file.formattedFileSize || EMPTY_CELL
    },
    ...classificationColumns,
    {
      title: t("files.columns.actions"),
      key: "actions",
      width: 80,
      render: (_, file) => (
        <Dropdown trigger={["click"]} menu={{ items: actions(file) }}>
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            aria-label={t("files.actions.menu", { name: file.name })}
          />
        </Dropdown>
      )
    }
  ]

  return (
    <Table<FileItem>
      rowKey="key"
      size="small"
      columns={columns}
      dataSource={items}
      pagination={false}
      rowSelection={
        selectedKeys && onToggleSelect
          ? {
              selectedRowKeys: [...selectedKeys],
              onSelect: (file) => onToggleSelect(file)
            }
          : undefined
      }
    />
  )
}
